import { useEffect } from "react";
import logo from "./logo.svg";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";

import Footer from "./layouts/footer";
import HomeScreen from "./pages/home";
import PaymentStatusScreen from "./pages/home/paymentStatus.js";
import LoginScreen from "./pages/login";
import RegisterScreen from "./pages/register";
import OtpScreen from "./pages/otp";
import BookingScreen from "./pages/booking";
import SingleBookingScreen from "./pages/singleBooking";
import EditProfileScreen from "./pages/editProfile";
import TableBookingScreen from "./pages/tableBooking";
import { CartProvider } from "./layouts/cart/CartContext";
import CartDrawer from "./layouts/cart/CartDrawer";
import RoomList from "./pages/modules/Room/RoomList";
import { AuthProvider, useAuth } from "./layouts/auth/AuthContext";
import RoomCheckout from "./pages/modules/Room/RoomCheckout";
import { RoomProvider } from "./pages/modules/Room/RoomContext";
import TableList from "./pages/modules/Table/TableList";
import { TableProvider } from "./pages/modules/Table/TableContext";
import TableCheckout from "./pages/modules/Table/TableCheckout";
import { VoucherProvider } from "./pages/modules/Voucher/VoucherContext";
import VoucherList from "./pages/modules/Voucher/VoucherList";
import VoucherCheckout from "./pages/modules/Voucher/VoucherCheckout";
import ProductList from "./pages/modules/Product/ProductList";
import { ProductProvider } from "./pages/modules/Product/ProductContext";
import ProductCheckout from "./pages/modules/Product/ProductCheckout";
import VoucherDetails from "./pages/modules/Voucher/VoucherDetails";
import ProductDetails from "./pages/modules/Product/ProductDetails";
import CheckoutPaymentStatus from "./layouts/checkout/CheckoutPaymentStatus";
import TableBookingList from "./pages/modules/Table/TableBookingList";
import VoucherBookingList from "./pages/modules/Voucher/VoucherBookingList";
import BookingMenuSelection from "./layouts/bookingMenuSelection";
import Booking from "./pages/booking";
import * as _ from "lodash";
import ProductBookingDetails from "./pages/modules/Product/ProductBookingDetails";
import { CheckoutProvider } from "./layouts/checkout/CheckoutContext";
import RegisterModal from "./layouts/auth/RegisterModal";
import LoginModal from "./layouts/auth/LoginModal";
import OtpVerificationModal from "./layouts/auth/OtpVerificationModal";
import VoucherTableBooking from "./pages/modules/Voucher/VoucherTableBooking";
import VoucherTableCheckout from "./pages/modules/Voucher/VoucherTableCheckout";
import { getSnapshot } from "mobx-state-tree";
import { mstAuth } from "./mobx";
import { observer } from "mobx-react-lite";
import VoucherTableGuestBooking from "./pages/modules/Voucher/VoucherTableGuestBooking";
import VoucherTableGuestCheckout from "./pages/modules/Voucher/VoucherTableGuestCheckout";

(window as any)._ = _;

const AppWrapper = () => {
  const { isLoggedIn, onOpenLoginModal } = useAuth();

  // Protected Route Component
  const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
    const { isLoggedIn, isLoading, user } = useAuth();

    console.log("ProtectedRoute - isLoggedIn:", isLoggedIn);
    console.log("ProtectedRoute - isLoading:", isLoading);
    console.log("ProtectedRoute - user:", user);
    console.log("ProtectedRoute - path:", window.location.pathname);

    if (isLoading) {
      console.log("ProtectedRoute - Showing loading state");
      return <div>Loading...</div>;
    }

    if (!isLoggedIn) {
      console.log("ProtectedRoute - User not logged in, redirecting to login");
      onOpenLoginModal();
      return <Navigate to="/room" replace />;
    }

    console.log("ProtectedRoute - User logged in, rendering children");
    return <>{children}</>;
  };

  // Public Route Component (redirect to home if already logged in)
  const PublicRoute = ({ children }: { children: React.ReactNode }) => {
    return !isLoggedIn ? children : <Navigate to="/room" replace />;
  };

  // Layout Component for authenticated pages
  // const AuthenticatedLayout = ({ children }: { children: React.ReactNode }) => {
  //   return (
  //     <>
  //       {/* You can add common layout for authenticated pages here */}
  //       {/* e.g., Header, Navigation, Sidebar, etc. */}
  //       {children}
  //     </>
  //   );
  // };

  // function App() {
  return (
    <>
      <LoginModal />
      <RegisterModal />
      <OtpVerificationModal />
      <CartProvider>
        <RoomProvider>
          <TableProvider>
            <VoucherProvider>
              <ProductProvider>
                <CheckoutProvider>
                  <CartDrawer />
                  <BrowserRouter>
                    <Routes>
                      {/* Public Routes - Accessible without authentication */}
                      <Route
                        path="/"
                        element={<Navigate to="/room" replace />}
                      />
                      <Route path="/room" element={<RoomList />} />
                      <Route path="/table" element={<TableList />} />
                      <Route path="/voucher" element={<VoucherList />} />
                      <Route
                        path="/voucher/guest-assign-slot"
                        element={<VoucherTableGuestBooking />}
                      />
                      <Route
                        path="/voucher/guest-checkout"
                        element={<VoucherTableGuestCheckout />}
                      />
                      <Route path="/voucher/:id" element={<VoucherDetails />} />
                      <Route path="/product" element={<ProductList />} />
                      <Route path="/product/:id" element={<ProductDetails />} />

                      {/* Auth Routes - Only for non-authenticated users */}
                      <Route
                        path="/login"
                        element={
                          <PublicRoute>
                            <LoginScreen />
                          </PublicRoute>
                        }
                      />
                      <Route
                        path="/register"
                        element={
                          <PublicRoute>
                            <RegisterScreen />
                          </PublicRoute>
                        }
                      />
                      <Route
                        path="/otp_verification"
                        element={
                          <PublicRoute>
                            <OtpScreen />
                          </PublicRoute>
                        }
                      />

                      <Route
                        path="/profile/edit"
                        element={
                          <ProtectedRoute>
                            {/* <AuthenticatedLayout> */}
                            <EditProfileScreen />
                            {/* </AuthenticatedLayout> */}
                          </ProtectedRoute>
                        }
                      />
                      {/* Specific paths first */}
                      <Route
                        path="/bookings"
                        element={
                          <ProtectedRoute>
                            <BookingScreen />
                          </ProtectedRoute>
                        }
                      />

                      {/* Dynamic paths in order of specificity */}
                      <Route
                        path="/bookings/product/:bookingId"
                        element={
                          <ProtectedRoute>
                            <ProductBookingDetails />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/voucher/:bookingId/assign-slot"
                        element={
                          <ProtectedRoute>
                            <VoucherTableBooking />
                          </ProtectedRoute>
                        }
                      />
                      {/* <Route
                        path="/bookings/:id"
                        element={
                          <ProtectedRoute>
                            <SingleBookingScreen />
                          </ProtectedRoute>
                        }
                      /> */}
                      {/* This should be LAST for bookings routes */}
                      <Route
                        path="/bookings/:moduleName"
                        element={
                          <ProtectedRoute>
                            <Booking />
                          </ProtectedRoute>
                        }
                      />

                      {/* Checkout Routes - Mixed accessibility */}
                      {/* Room checkout can be public (guest checkout) or protected */}
                      <Route path="/room/checkout" element={<RoomCheckout />} />
                      <Route
                        path="/table/checkout"
                        element={<TableCheckout />}
                      />
                      <Route
                        path="/voucher/checkout/:bookingId"
                        element={
                          <ProtectedRoute>
                            <VoucherCheckout />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/voucher/:bookingId/assign-slot/checkout"
                        element={
                          <ProtectedRoute>
                            <VoucherTableCheckout />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/product/checkout/:bookingId"
                        element={
                          <ProtectedRoute>
                            <ProductCheckout />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/checkout/payment_status/:bookingId"
                        element={<CheckoutPaymentStatus />}
                      />

                      {/* Fallback route */}
                      <Route path="*" element={<Navigate to="" replace />} />
                    </Routes>
                  </BrowserRouter>
                </CheckoutProvider>
              </ProductProvider>
            </VoucherProvider>
          </TableProvider>
        </RoomProvider>
      </CartProvider>
    </>
  );
  // }
};

const App = () => {
  return (
    <AuthProvider>
      <AppWrapper />
    </AuthProvider>
  );
};

export default App;
